import { Link } from "react-router-dom";
import Section from "../components/ui/Section";
import FlowSteps from "../components/ui/FlowSteps";

const About = () => {
  return (
    <div className="bg-bg">
      <Section
        eyebrow="About NOVTIA"
        title="We build governed AI systems for complex operations."
        description="NOVTIA combines AI strategy, engineering, governance, and proprietary infrastructure. We help organisations redesign processes, build and integrate dependable systems, and operate them with the controls and evidence required in production."
      >
        <p className="text-sm text-muted max-w-2xl">
          Our work spans the full journey from initial discovery to ongoing
          operation — but every engagement is shaped around the client&apos;s
          actual problem, existing technology and level of readiness.
        </p>
      </Section>

      {/* WHO NOVTIA IS */}
      <section className="border-t border-line">
        <Section
          eyebrow="Who we are"
          title="Built for the gap between AI ambition and operational reality."
        >
          <div className="grid md:grid-cols-2 gap-8 text-sm">
            <p className="text-ink">
              Many organisations have invested in data platforms, enterprise
              software and AI experimentation, yet their most important
              workflows remain fragmented, manual or difficult to govern.
              NOVTIA exists to close that gap.
            </p>
            <p className="text-muted">
              We combine business and process analysis, AI product
              architecture, production engineering and runtime governance.
              This allows us to address not only whether a model works, but
              whether the complete system improves the organisation,
              integrates with existing operations, protects sensitive
              information and behaves safely after deployment.
            </p>
          </div>
        </Section>
      </section>

      {/* WHAT NOVTIA IS NOT */}
      <section className="border-t border-line">
        <Section eyebrow="A clear boundary" title="We do not replace your enterprise foundation.">
          <div className="surface-card p-6 sm:p-7 text-sm space-y-3">
            <p className="text-ink">
              NOVTIA is not an alternative to SAP, Salesforce, Microsoft,
              Oracle or the other platforms on which organisations run.
            </p>
            <p className="text-muted">
              We work across and around existing systems — redesigning
              workflows, connecting fragmented data and applications, and
              introducing the appropriate level of intelligence, automation
              and governance.
            </p>
            <p className="text-muted">
              Sometimes the right answer is an API integration or
              deterministic workflow. Sometimes it is predictive machine
              learning, document intelligence, an AI assistant or an agentic
              system. Our role is to choose and implement what the problem
              genuinely requires.
            </p>
          </div>
        </Section>
      </section>

      {/* HOW NOVTIA WORKS */}
      <section className="border-t border-line">
        <Section
          eyebrow="Lifecycle"
          title="From operational problem to dependable system."
        >
          <ol className="lifecycle">
            {[
              {
                label: "01",
                title: "Understand",
                body: "We examine the business objective, existing workflow, users, data, technology, risks and constraints.",
              },
              {
                label: "02",
                title: "Redesign",
                body: "We map the current state, identify gaps and create a better target process before automating it.",
              },
              {
                label: "03",
                title: "Architect",
                body: "We determine the right combination of software, integration, data infrastructure, machine learning, LLMs, agentic workflows and human decision-making.",
              },
              {
                label: "04",
                title: "Build and integrate",
                body: "We develop the solution and connect it to the organisation's existing platforms, APIs, databases and operating environment.",
              },
              {
                label: "05",
                title: "Govern",
                body: "We introduce access controls, policy enforcement, privacy protections, human approval, auditability and runtime intervention.",
              },
              {
                label: "06",
                title: "Operate and improve",
                body: "We monitor reliability, quality, cost, risk and business outcomes, then continuously improve both the system and the underlying workflow.",
              },
            ].map((step) => (
              <li key={step.label} className="lifecycle-step">
                <span className="lifecycle-marker tabular">{step.label}</span>
                <h3 className="font-display text-lg font-semibold text-ink mb-1.5">
                  {step.title}
                </h3>
                <p className="text-sm text-muted max-w-2xl">{step.body}</p>
              </li>
            ))}
          </ol>
        </Section>
      </section>

      {/* PRINCIPLES */}
      <section className="border-t border-line">
        <Section eyebrow="Discipline" title="Principles that shape our work">
          <div className="surface-card grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-line">
            {[
              {
                title: "Process before technology",
                body: "We do not automate a broken workflow without first questioning how it should work.",
              },
              {
                title: "Appropriate intelligence",
                body: "We use deterministic software, ML, generative AI or agents according to the problem — not according to fashion.",
              },
              {
                title: "Existing systems first",
                body: "We build on the organisation's existing investments wherever that is practical and responsible.",
              },
              {
                title: "Governed autonomy",
                body: "Consequential actions are checked against policy, risk, and identity before they execute — not reviewed afterward. AI systems receive only the access and authority their role requires.",
              },
              {
                title: "Privacy by architecture",
                body: "Data minimisation, regional deployment, access control and retention requirements are designed into the system from the start.",
              },
              {
                title: "Production evidence over demonstrations",
                body: "A useful AI system must remain reliable, observable and economically sustainable after the prototype.",
              },
            ].map((p) => (
              <div key={p.title} className="p-6">
                <h3 className="font-display text-sm font-semibold text-ink mb-2">
                  {p.title}
                </h3>
                <p className="text-sm text-muted">{p.body}</p>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* THREE DISCIPLINES */}
      <section className="border-t border-line">
        <Section
          eyebrow="One delivery model"
          title="Three disciplines, one delivery model"
        >
          <div className="surface-card grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-line">
            <div className="p-6">
              <p className="kicker mb-3">Strategy</p>
              <p className="text-sm text-muted">
                Business analysis, process redesign, AI opportunity
                assessment, operating-model definition and transformation
                planning.
              </p>
            </div>
            <div className="p-6">
              <p className="kicker mb-3">Governance</p>
              <p className="text-sm text-muted">
                Governance, policy controls, observability, privacy,
                explainability, private deployment and human oversight.
              </p>
            </div>
            <div className="p-6">
              <p className="kicker mb-3">Engineering</p>
              <p className="text-sm text-muted">
                Product design, AI/ML development, agentic architecture,
                integrations, cloud infrastructure and production
                operations.
              </p>
            </div>
          </div>
          <p className="text-sm text-muted mt-6">
            Clients can engage NOVTIA for one focused capability or for an
            end-to-end programme combining all three.
          </p>
        </Section>
      </section>

      {/* TECHNOLOGY FOUNDATION */}
      <section className="border-t border-line">
        <Section
          eyebrow="Our technology foundation"
          title="One foundation behind products and client systems."
        >
          <div className="text-sm space-y-3 max-w-3xl mb-10">
            <p className="text-ink">
              NOVTIA develops proprietary infrastructure for building and
              operating dependable AI systems. At the centre of this work is{" "}
              <span className="font-semibold">NOVTIA Sovereign Core</span>, a
              reusable cognitive and control foundation combining
              orchestration, governance, structured reasoning and
              neuro-symbolic capabilities.
            </p>
            <p className="text-muted">
              The Sovereign Core is not a standalone product customers need to
              adopt. It is the foundation NOVTIA applies through its products,
              bespoke client solutions, and delivery work.
            </p>
            <p className="text-muted">
              The Core allows NOVTIA to carry consistent operational
              principles across products and client systems: bounded
              autonomy, policy-aware execution, observability, human
              oversight, privacy and sovereign deployment.
            </p>
            <p className="text-muted">
              It does not replace the organisation&apos;s existing
              enterprise platforms. It provides an intelligence and control
              foundation that can integrate with those systems and
              coordinate AI-enabled operations across them.
            </p>
          </div>

          <p className="kicker mb-4 justify-center md:justify-start">
            How the pieces connect
          </p>
          <FlowSteps
            steps={[
              {
                label: "Client business and operational environment",
                detail: "SAP · Salesforce · Microsoft · Databases · Internal systems",
              },
              {
                label: "NOVTIA services",
                detail: "Strategy · Process redesign · Engineering · Integration · Operations",
              },
              {
                label: "NOVTIA Sovereign Core",
                detail: "Orchestration · Governance · Reasoning · Neuro-symbolic intelligence",
              },
              {
                label: "NOVTIA products and client solutions",
                detail: "AIRG · Future products · Bespoke governed AI systems",
              },
            ]}
          />

          <div className="text-sm space-y-3 max-w-3xl mt-10">
            <p className="text-muted">
              <span className="font-semibold text-ink">NOVTIA Labs</span>{" "}
              explores recurring challenges in agentic systems, AI
              governance, observability and infrastructure. Its role is to
              create reusable engineering capabilities that improve how
              NOVTIA solutions are built and operated. Work from the Labs
              includes runtime policy enforcement, agent-risk evaluation,
              auditable decision records, orchestration patterns and
              infrastructure for controlled AI execution.
            </p>
          </div>
        </Section>
      </section>

      {/* SECTOR EXPERIENCE */}
      <section className="border-t border-line">
        <Section eyebrow="Where we work" title="Particularly suited to complex, high-stakes and regulated environments.">
          <p className="text-sm text-muted max-w-2xl">
            NOVTIA&apos;s experience spans financial services, utilities and
            critical infrastructure, legal technology, multilingual media
            operations, enterprise intelligence and emerging agentic
            systems.
          </p>
        </Section>
      </section>

      {/* CTA */}
      <section className="border-t border-line">
        <Section>
          <div className="surface-card p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <p className="kicker mb-3">Start with the operating context</p>
              <h2 className="font-display text-xl sm:text-2xl font-semibold text-ink mb-2">
                Bring us the operational problem — not a predetermined AI
                solution.
              </h2>
              <p className="text-sm text-muted max-w-xl">
                We will help determine what should change, what should
                remain human, which technology is appropriate and how the
                resulting system can be deployed responsibly.
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center bg-signal px-6 py-2.5 text-sm font-semibold text-on-signal hover:bg-signal-strong transition-colors flex-none"
            >
              Start a conversation
            </Link>
          </div>
        </Section>
      </section>
    </div>
  );
};

export default About;
